/**
 * activeEcErrors.ts
 *
 * Registry of ECs that are CURRENTLY sitting in their error-boundary fallback
 * (the "Something went wrong" panel). It gives the agent the peripheral vision a
 * human has: between tool calls the agent cannot see the screen, so a crashed EC
 * would otherwise go unnoticed. The tool loop appends `formatActiveEcErrors` onto
 * every tool result, and `retry_ec` drives recovery via the stored `reset`.
 *
 * This is deliberately ACTIVE STATE, not an event log: entries are added when a
 * boundary catches and removed when it resets/unmounts. A transient crash that
 * has already self-recovered simply isn't here — so the agent never chases a
 * ghost, and retrying an already-cleared error is a no-op.
 *
 * Plain module store (not a jotai atom): the only reader is the tool layer, which
 * runs outside React and reads synchronously. The UI does not render this.
 */

import type { ECId } from "@/endlaFunctions/customTypings/ecTypes";

export type ActiveEcError = {
  /** Stable per-boundary id (one error-boundary instance owns one entry). */
  id: string;
  ecId?: ECId;
  /** The boundary's `dataError` label, e.g. "wellPlot-123". */
  label?: string;
  message: string;
  occurredAt: number;
  /**
   * Soft reset for this boundary — re-renders its children without reloading the
   * page or wiping caches. Implemented by the boundary bumping a `resetKeys` nonce.
   */
  reset: () => void;
};

const store = new Map<string, ActiveEcError>();

export function registerEcError(error: ActiveEcError): void {
  store.set(error.id, error);
}

export function clearEcError(id: string): void {
  store.delete(id);
}

export function getActiveEcErrors(): ActiveEcError[] {
  return [...store.values()];
}

export type RetryEcErrorsResult = {
  retried: { ecId?: ECId; label?: string }[];
  /** Active errors still present shortly after the reset attempt. */
  remaining: { ecId?: ECId; label?: string; message: string }[];
};

/**
 * Trigger a soft reset on active errored boundaries — all of them, or only those
 * matching `ecIds`. Waits `delayMs` first so a transient race (the agent acting
 * faster than state settled) has a moment to resolve before we re-render. The
 * boundaries remove their own entries via `clearEcError` when they reset, so a
 * brief settle pass after triggering reports whether recovery actually stuck.
 */
export async function retryActiveEcErrors(opts?: {
  ecIds?: ECId[];
  delayMs?: number;
}): Promise<RetryEcErrorsResult> {
  const { ecIds, delayMs = 1000 } = opts ?? {};
  const targets = getActiveEcErrors().filter(
    (e) => ecIds === undefined || (e.ecId !== undefined && ecIds.includes(e.ecId))
  );

  await sleep(delayMs);
  for (const target of targets) {
    target.reset();
  }
  // Let the reset propagate (componentDidUpdate → onReset → clearEcError) before
  // sampling what remains.
  await sleep(50);

  return {
    retried: targets.map((e) => ({ ecId: e.ecId, label: e.label })),
    remaining: getActiveEcErrors().map((e) => ({
      ecId: e.ecId,
      label: e.label,
      message: e.message,
    })),
  };
}

/**
 * Compact note appended to tool results while any EC is errored. Empty string
 * when nothing is active, so callers can append unconditionally.
 */
export function formatActiveEcErrors(): string {
  const active = getActiveEcErrors();
  if (active.length === 0) {
    return "";
  }
  const lines = active.map((e) => {
    const who = e.ecId !== undefined ? `ec ${e.ecId}` : (e.label ?? "unknown EC");
    const label = e.label && e.ecId !== undefined ? ` (${e.label})` : "";
    return `- ${who}${label}: "${e.message}"`;
  });
  return (
    `\n\n⚠ ACTIVE EC ERRORS (${active.length}): a component on the dashboard is currently in an error state.\n` +
    `${lines.join("\n")}\n` +
    `Call retry_ec to attempt recovery (soft reset, no data loss), or continue if it's unrelated to your task.`
  );
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
